import { useEffect, useState, type FormEvent } from 'react'

import { Button } from '#app/components/ui/button.tsx'
import { ResponsiveOverlay } from '#app/components/ui/responsive-overlay.tsx'

import { TRAINING_PROGRAM_COPY } from '#app/routes/mocra+/_form+/components/training-programs/constants.ts'
import { TrainingProgramForm } from '#app/routes/mocra+/_form+/components/training-programs/form.tsx'
import {
	collectTrainingProgramErrors,
	createTrainingProgramDraft,
	sanitizeTrainingProgramDraft,
	type TrainingProgramDraft,
} from '#app/routes/mocra+/_form+/components/training-programs/types.ts'

export type TrainingProgramOverlayMode = 'add' | 'edit'

type DraftErrors = Partial<Record<keyof TrainingProgramDraft, string>>

type Props = {
	readonly mode: TrainingProgramOverlayMode
	readonly open: boolean
	readonly onOpenChange: (open: boolean) => void
	readonly onSubmit: (draft: TrainingProgramDraft) => void
	readonly initialValue?: TrainingProgramDraft
	readonly onCancel?: () => void
	readonly onClosed?: () => void
}

export function TrainingProgramOverlay({
	mode,
	open,
	onOpenChange,
	onSubmit,
	initialValue,
	onCancel,
	onClosed,
}: Props) {
	const [draft, setDraft] = useState<TrainingProgramDraft>(
		initialValue ? { ...initialValue } : createTrainingProgramDraft(),
	)
	const [errors, setErrors] = useState<DraftErrors>({})

	useEffect(() => {
		if (open) {
			setDraft(
				initialValue ? { ...initialValue } : createTrainingProgramDraft(),
			)
			setErrors({})
		}
	}, [open, initialValue])

	useEffect(() => {
		if (!open) {
			onClosed?.()
		}
	}, [onClosed, open])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		event.stopPropagation()
		const sanitized = sanitizeTrainingProgramDraft(draft)
		const validationErrors = collectTrainingProgramErrors(sanitized)
		if (validationErrors.length > 0) {
			const nextErrors: DraftErrors = {}
			nextErrors.platform = validationErrors[0]
			setErrors(nextErrors)
			return
		}
		onSubmit(sanitized)
	}

	const handleCancel = () => {
		onOpenChange(false)
		onCancel?.()
	}

	const titleCopy =
		mode === 'add'
			? TRAINING_PROGRAM_COPY.title.add
			: TRAINING_PROGRAM_COPY.title.edit
	const submitCopy =
		mode === 'add'
			? TRAINING_PROGRAM_COPY.submit.add
			: TRAINING_PROGRAM_COPY.submit.edit

	return (
		<ResponsiveOverlay
			open={open}
			onOpenChange={onOpenChange}
			title={titleCopy}
			description={TRAINING_PROGRAM_COPY.description}
			footer={
				<div className="flex justify-end gap-3">
					<Button type="button" variant="outline" onClick={handleCancel}>
						Cancel
					</Button>
					<Button type="submit" form="training-program-overlay-form">
						{submitCopy}
					</Button>
				</div>
			}
		>
			<TrainingProgramForm
				formId="training-program-overlay-form"
				draft={draft}
				errors={errors}
				onDraftFieldChange={(field, value) =>
					setDraft((prev) => ({ ...prev, [field]: value }))
				}
				onSubmit={handleSubmit}
			/>
		</ResponsiveOverlay>
	)
}
